import { useCallback, useEffect, useLayoutEffect, useRef, useState, } from 'react';
const VIEWPORT_MARGIN = 12;
const FORM_GAP = 12;
const DEFAULT_WIDTH = 400;
const DEFAULT_HEIGHT = 560;
const MIN_WIDTH = 280;
const MIN_HEIGHT = 320;
const LAYOUT_STORAGE_KEY = 'wabta_portal_smart_form_chat_layout_v1';
function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}
function getFallbackPosition(height) {
    if (typeof window === 'undefined') {
        return { x: VIEWPORT_MARGIN, y: 120 };
    }
    return {
        x: VIEWPORT_MARGIN,
        y: clamp(window.innerHeight - height - VIEWPORT_MARGIN, VIEWPORT_MARGIN, 160),
    };
}
/** Place the chat immediately to the left of the survey form, with a small gap. */
function getSnugPosition(width, height, anchor) {
    if (typeof window === 'undefined' || !anchor) {
        return getFallbackPosition(height);
    }
    const rect = anchor.getBoundingClientRect();
    if (rect.width <= 0) {
        return getFallbackPosition(height);
    }
    const x = clamp(Math.round(rect.left - width - FORM_GAP), VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
    const y = clamp(Math.round(rect.top), VIEWPORT_MARGIN, window.innerHeight - height - VIEWPORT_MARGIN);
    return { x, y };
}
function getMaxSize(x, y) {
    return {
        maxWidth: window.innerWidth - x - VIEWPORT_MARGIN,
        maxHeight: window.innerHeight - y - VIEWPORT_MARGIN,
    };
}
function readStoredLayout() {
    if (typeof window === 'undefined')
        return null;
    try {
        const raw = window.localStorage.getItem(LAYOUT_STORAGE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed.x !== 'number' ||
            typeof parsed.y !== 'number' ||
            typeof parsed.width !== 'number' ||
            typeof parsed.height !== 'number') {
            return null;
        }
        return {
            x: parsed.x,
            y: parsed.y,
            width: parsed.width,
            height: parsed.height,
            userMoved: Boolean(parsed.userMoved),
        };
    }
    catch {
        return null;
    }
}
function writeStoredLayout(layout) {
    if (typeof window === 'undefined')
        return;
    try {
        window.localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout));
    }
    catch {
        // localStorage can be unavailable in restricted browser contexts.
    }
}
function getInitialLayout() {
    const stored = readStoredLayout();
    const width = stored?.width ?? DEFAULT_WIDTH;
    const height = stored?.height ?? DEFAULT_HEIGHT;
    if (typeof window === 'undefined') {
        return {
            x: VIEWPORT_MARGIN,
            y: 120,
            width,
            height,
            userMoved: Boolean(stored?.userMoved),
        };
    }
    const size = {
        width: clamp(width, MIN_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2),
        height: clamp(height, MIN_HEIGHT, window.innerHeight - VIEWPORT_MARGIN * 2),
    };
    if (stored?.userMoved) {
        return {
            ...size,
            x: clamp(stored.x, VIEWPORT_MARGIN, window.innerWidth - size.width - VIEWPORT_MARGIN),
            y: clamp(stored.y, VIEWPORT_MARGIN, window.innerHeight - size.height - VIEWPORT_MARGIN),
            userMoved: true,
        };
    }
    const fallback = getFallbackPosition(size.height);
    return {
        ...size,
        x: fallback.x,
        y: fallback.y,
        userMoved: false,
    };
}
export function useDraggablePanel(anchorRef) {
    const initial = useRef(getInitialLayout()).current;
    const panelRef = useRef(null);
    const [position, setPosition] = useState({ x: initial.x, y: initial.y });
    const [size, setSize] = useState({ width: initial.width, height: initial.height });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const userMovedRef = useRef(initial.userMoved);
    const positionRef = useRef(position);
    const sizeRef = useRef(size);
    positionRef.current = position;
    sizeRef.current = size;
    const persistLayout = useCallback(() => {
        writeStoredLayout({
            x: positionRef.current.x,
            y: positionRef.current.y,
            width: sizeRef.current.width,
            height: sizeRef.current.height,
            userMoved: userMovedRef.current,
        });
    }, []);
    const clampPosition = useCallback((x, y, width, height) => {
        return {
            x: clamp(x, VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN),
            y: clamp(y, VIEWPORT_MARGIN, window.innerHeight - height - VIEWPORT_MARGIN),
        };
    }, []);
    const clampSize = useCallback((width, height, x, y) => {
        const { maxWidth, maxHeight } = getMaxSize(x, y);
        return {
            width: clamp(width, MIN_WIDTH, Math.max(MIN_WIDTH, maxWidth)),
            height: clamp(height, MIN_HEIGHT, Math.max(MIN_HEIGHT, maxHeight)),
        };
    }, []);
    const dragState = useRef(null);
    const resizeState = useRef(null);
    const snugToAnchor = useCallback((width, height) => {
        const next = getSnugPosition(width, height, anchorRef?.current ?? null);
        return clampPosition(next.x, next.y, width, height);
    }, [anchorRef, clampPosition]);
    const onTitlePointerDown = useCallback((event) => {
        if (event.button !== 0)
            return;
        event.preventDefault();
        dragState.current = {
            startX: event.clientX,
            startY: event.clientY,
            originX: position.x,
            originY: position.y,
        };
        setIsDragging(true);
        event.currentTarget.setPointerCapture(event.pointerId);
    }, [position.x, position.y]);
    const onTitlePointerMove = useCallback((event) => {
        if (!dragState.current)
            return;
        const dx = event.clientX - dragState.current.startX;
        const dy = event.clientY - dragState.current.startY;
        if (dx !== 0 || dy !== 0) {
            userMovedRef.current = true;
        }
        setPosition(clampPosition(dragState.current.originX + dx, dragState.current.originY + dy, size.width, size.height));
    }, [clampPosition, size.width, size.height]);
    const endDrag = useCallback((event) => {
        if (!dragState.current)
            return;
        dragState.current = null;
        setIsDragging(false);
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        persistLayout();
    }, [persistLayout]);
    const onResizePointerDown = useCallback((event) => {
        if (event.button !== 0)
            return;
        event.preventDefault();
        event.stopPropagation();
        resizeState.current = {
            startX: event.clientX,
            startY: event.clientY,
            originWidth: size.width,
            originHeight: size.height,
        };
        setIsResizing(true);
        event.currentTarget.setPointerCapture(event.pointerId);
    }, [size.width, size.height]);
    const onResizePointerMove = useCallback((event) => {
        if (!resizeState.current)
            return;
        const dx = event.clientX - resizeState.current.startX;
        const dy = event.clientY - resizeState.current.startY;
        setSize(clampSize(resizeState.current.originWidth + dx, resizeState.current.originHeight + dy, position.x, position.y));
    }, [clampSize, position.x, position.y]);
    const endResize = useCallback((event) => {
        if (!resizeState.current)
            return;
        resizeState.current = null;
        setIsResizing(false);
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        persistLayout();
    }, [persistLayout]);
    // Initial / layout snug once the form is measurable (unless user placed it).
    useLayoutEffect(() => {
        if (userMovedRef.current)
            return;
        setPosition(snugToAnchor(size.width, size.height));
    }, [snugToAnchor, size.width, size.height]);
    // Keep snugged when the form or viewport resizes, until the user drags the panel.
    useEffect(() => {
        const handleViewportResize = () => {
            setSize((prev) => {
                const nextSize = clampSize(prev.width, prev.height, position.x, position.y);
                if (!userMovedRef.current) {
                    setPosition(snugToAnchor(nextSize.width, nextSize.height));
                }
                else {
                    setPosition((pos) => clampPosition(pos.x, pos.y, nextSize.width, nextSize.height));
                }
                return nextSize;
            });
        };
        window.addEventListener('resize', handleViewportResize);
        const anchor = anchorRef?.current;
        let observer = null;
        if (anchor && typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(() => {
                if (userMovedRef.current)
                    return;
                setPosition((prev) => {
                    const next = snugToAnchor(size.width, size.height);
                    if (next.x === prev.x && next.y === prev.y)
                        return prev;
                    return next;
                });
            });
            observer.observe(anchor);
        }
        return () => {
            window.removeEventListener('resize', handleViewportResize);
            observer?.disconnect();
        };
    }, [
        anchorRef,
        clampPosition,
        clampSize,
        position.x,
        position.y,
        size.width,
        size.height,
        snugToAnchor,
    ]);
    const panelStyle = {
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
    };
    return {
        panelRef,
        panelStyle,
        isDragging,
        isResizing,
        onTitlePointerDown,
        onTitlePointerMove,
        onTitlePointerUp: endDrag,
        onTitlePointerCancel: endDrag,
        onResizePointerDown,
        onResizePointerMove,
        onResizePointerUp: endResize,
        onResizePointerCancel: endResize,
    };
}
